/* ==========================================================================
   QUANTITY PRICING / VOLUME PRICING (NEW FILE)
   Custom element <volume-pricing>, rendered by the "Quantity pricing" block of
   sections/main-product.liquid.

   Data comes from Shopify's B2B quantity pricing
   (https://help.shopify.com/en/manual/b2b/catalogs/quantity-pricing):
   variant.quantity_rule (min / increment / max) and variant.quantity_price_breaks.

   - Shows the order rules and the price-break table for the SELECTED variant.
   - Follows variant changes (the theme re-renders the variant picker and publishes the
     selected variant as JSON, which is watched here) and quantity changes
     (`quantity-change` event of <product-quantity>).
   - Highlights the active tier and shows the total for the chosen quantity.
   - Read-only: it never changes the cart or the theme's own price display.
   ========================================================================== */

(function () {
  if (customElements.get('volume-pricing')) return;

  class VolumePricing extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.moneyFormat = this.dataset.moneyFormat;
      this.rulesEl = this.querySelector('[data-vp-rules]');
      this.tableEl = this.querySelector('[data-vp-table]');
      this.totalEl = this.querySelector('[data-vp-total]');

      try {
        this.variants = JSON.parse(this.querySelector('[data-vp-data]').textContent);
      } catch (error) {
        console.error('[VolumePricing] invalid data', error);
        return;
      }

      this.shell = document.querySelector(`pdp-product-shell[data-section-id="${this.sectionId}"]`);
      this.variantId = Number(this.dataset.selectedVariant);
      this.quantity = this.readQuantity();

      // Quantity changed with the +/- buttons or by typing
      this.onQuantityChange = (e) => {
        this.quantity = Number(e.detail?.value) || this.readQuantity();
        this.render();
      };
      this.shell?.addEventListener('quantity-change', this.onQuantityChange);

      // The theme swaps the variant picker (and its [data-selected-variant] JSON) after every
      // variant change, so DOM changes inside the product shell tell us when to re-read it.
      if (this.shell && 'MutationObserver' in window) {
        this.observer = new MutationObserver(() => {
          if (this._raf) return;
          this._raf = requestAnimationFrame(() => {
            this._raf = null;
            this.readSelectedVariant();
          });
        });
        this.observer.observe(this.shell, { childList: true, subtree: true });
      }

      this.render();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.shell?.removeEventListener('quantity-change', this.onQuantityChange);
      this._initialized = false;
    }

    /* ---------- State ---------- */

    get variant() {
      return this.variants.find((v) => v.id === this.variantId) || this.variants[0];
    }

    /** Quantity currently chosen in the main product form (falls back to the variant minimum) */
    readQuantity() {
      const input = this.shell?.querySelector('.pdp-atc-shell input[name="quantity"]');
      const value = Number(input?.value);
      return value > 0 ? value : this.variant?.min || 1;
    }

    readSelectedVariant() {
      const json = this.shell?.querySelector('[data-selected-variant]');
      if (!json) return;
      try {
        const id = JSON.parse(json.textContent).id;
        if (id && id !== this.variantId) {
          this.variantId = id;
          this.quantity = this.readQuantity();
          this.render();
        }
      } catch (error) {
        /* ignore malformed JSON */
      }
    }

    /** Price per item (cents) that applies at a given quantity: highest break whose quantity is reached */
    unitPriceFor(variant, quantity) {
      let price = variant.price;
      variant.breaks.forEach((brk) => {
        if (quantity >= brk.quantity) price = brk.price;
      });
      return price;
    }

    money(cents) {
      return window.Shopify?.formatMoney
        ? window.Shopify.formatMoney(cents, this.moneyFormat)
        : (cents / 100).toFixed(2);
    }

    /* ---------- Rendering ---------- */

    render() {
      const variant = this.variant;
      if (!variant) return;

      this.renderRules(variant);
      this.renderTable(variant);
    }

    /** "Minimum order: 6 · Multiples of 6 · Maximum: 60" */
    renderRules(variant) {
      const parts = [];
      if (variant.min > 1) parts.push(`${this.dataset.textMin}: ${variant.min}`);
      if (variant.increment > 1) parts.push(`${this.dataset.textMultiples} ${variant.increment}`);
      if (variant.max > 0) parts.push(`${this.dataset.textMax}: ${variant.max}`);

      this.rulesEl.textContent = parts.join(' · ');
      this.rulesEl.hidden = parts.length === 0;
    }

    /** Price-break table with the active tier highlighted, plus the total for the chosen quantity */
    renderTable(variant) {
      if (!variant.breaks.length) {
        this.tableEl.hidden = true;
        this.totalEl.hidden = true;
        this.tableEl.textContent = '';
        return;
      }

      // First row is the regular price from the minimum order quantity, then one row per break
      const tiers = [{ quantity: variant.min || 1, price: variant.price }, ...variant.breaks];
      const unit = this.unitPriceFor(variant, this.quantity);
      let activeIndex = 0;
      tiers.forEach((tier, i) => {
        if (this.quantity >= tier.quantity) activeIndex = i;
      });

      const table = document.createElement('table');
      table.className = 'volume_pricing__table';

      const head = table.createTHead().insertRow();
      [this.dataset.textQuantity, this.dataset.textPrice].forEach((text) => {
        const th = document.createElement('th');
        th.scope = 'col';
        th.textContent = text;
        head.appendChild(th);
      });

      const body = table.createTBody();
      tiers.forEach((tier, i) => {
        const row = body.insertRow();
        row.className = i === activeIndex ? 'is-active' : '';
        row.insertCell().textContent = `${tier.quantity}+`;
        row.insertCell().textContent = this.money(tier.price);
      });

      this.tableEl.replaceChildren(table);
      this.tableEl.hidden = false;

      // e.g. "Total for 12: $144.00 ($12.00 each)"
      this.totalEl.textContent = `${this.dataset.textTotal} ${this.quantity}: ${this.money(
        unit * this.quantity
      )} (${this.money(unit)} ${this.dataset.textEach})`;
      this.totalEl.hidden = false;
    }
  }

  customElements.define('volume-pricing', VolumePricing);
})();
